import { initInfo, initState } from './init';
import type {
  DocumentReviewAction,
  DocumentReviewTrigger
} from '../types/Form';

function isValidUrl(urlString: string): boolean {
  try {
    const url = new URL(urlString);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

// Everyone who can sign has a signer row, so an envelope is always opened as
// that one signer rather than left for the server to guess.
export function getSignUrl(token: string, redirect?: boolean | string) {
  const regionPart =
    initState.region && initState.region !== 'us' ? `${initState.region}.` : '';

  let query = '';
  if (redirect) {
    if (typeof redirect === 'string') {
      if (!isValidUrl(redirect)) {
        console.error(
          `Invalid redirect URL: "${redirect}". Must be a full URL with http:// or https://`
        );
      } else {
        query = `?redirect=${encodeURIComponent(redirect)}`;
      }
    } else {
      // If redirect is true (boolean), redirect back to form
      const url = new URL(location.href);
      if (!url.searchParams.has('_id')) {
        url.searchParams.append('_id', initInfo().userId ?? '');
      }
      query = `?redirect=${encodeURIComponent(url.toString())}`;
    }
  }

  return `https://${regionPart}document.feathery.io/to/${token}${query}`;
}

// The file upload fields a Generate Documents action includes, with the step
// each is on. Empty fields stay in so a removed upload can be cleared.
export function documentSourceUploadFields(
  documents: any[] = [],
  steps: Record<string, any> = {}
): { servar: any; step: any }[] {
  const fieldIds = new Set(
    documents
      .filter((doc) => doc?.kind === 'file_upload')
      .map((doc) => doc.field_id)
  );
  const fields: { servar: any; step: any }[] = [];
  if (!fieldIds.size) return fields;
  for (const step of Object.values(steps) as any[]) {
    for (const { servar } of step?.servar_fields ?? []) {
      if (servar.type !== 'file_upload' || !fieldIds.has(servar.id)) continue;
      fieldIds.delete(servar.id);
      fields.push({ servar, step });
    }
  }
  return fields;
}

/** The Document Editor container this action targets, or '' when it doesn't
 * target one. `editor_mode` is the single source of truth for how the editor is
 * presented: '' (none), 'overlay', or a container id. */
export function editorContainerId(action: Record<string, any>): string {
  const mode = action?.editor_mode;
  return mode && mode !== 'overlay' ? mode : '';
}

/** What a Document Editor container's toolbar offers, read from the same
 * `editor_toolbar_actions` key the overlay uses — `envelope_action` is always
 * 'open_in_editor' for a container and carries no outcome of its own.
 *
 * One terminal button, so the most conclusive outcome wins (Sign > Create Draft
 * > Download) and `offersDraft` puts the other signing outcome in a menu beside
 * it. 'draft' needs DocuSign; nothing else has a draft state. `offersDownload`
 * keeps the secondary Download button available when a signing outcome owns
 * the terminal slot but Download is also configured.
 */
export function containerToolbarOutcomes(action: Record<string, any>): {
  terminalAction: 'sign' | 'download' | 'draft' | undefined;
  offersDraft: boolean;
  offersDownload: boolean;
  savesToField: boolean;
} {
  const actions: string[] = action?.editor_toolbar_actions ?? [];
  const draft = actions.includes('draft') && action?.sign_method === 'docusign';
  const sign = actions.includes('sign');
  return {
    terminalAction: sign
      ? 'sign'
      : draft
      ? 'draft'
      : actions.includes('download')
      ? 'download'
      : undefined,
    // Only meaningful beside Sign; on its own, draft *is* the terminal action.
    offersDraft: draft && sign,
    offersDownload: actions.includes('download'),
    savesToField: actions.includes('save')
  };
}

// Generate Documents `sign` actions default to Feathery's own hosted eSign
// flow (`getSignUrl` above). When the action is configured for DocuSign
// instead, the backend's `{docusign_envelope_id, status}` response *is* the
// completion signal — there is no Feathery sign URL to redirect to, so
// callers must skip that redirect entirely and just continue the flow.
// The action has to actually resolve to signing, because `sign_method` is only
// meaningful for the sign action and the designer leaves a previously chosen
// `sign_method` on the action when the envelope action is switched away from
// sign. Matching the backend (which ignores sign_method unless the action
// resolves to sign) keeps a stale value from rerouting fill/download/save
// through the docusign path, where merge_docs would be silently dropped.
//
// `actingAction` is what resolves it. In the editor flow `action.envelope_action`
// is 'open_in_editor' and carries no outcome of its own — the outcome is the
// toolbar button the filler pressed, which arrives here as `actingAction`.
// Reading only `action.envelope_action` answered "not DocuSign" for every editor
// Sign / Save-as-Draft press, so the caller sent the envelope through DocuSign
// and then *also* opened Feathery's hosted eSign page (navigating the page away
// outright when `action.redirect` was set).
export function isDocusignSignAction(
  action: Record<string, any>,
  actingAction?: string
): boolean {
  const envelopeAction = actingAction ?? action?.envelope_action;
  return (
    action?.sign_method === 'docusign' &&
    (!envelopeAction || envelopeAction === 'sign')
  );
}

/** Whether this action could reach DocuSign at all, mirroring the backend's own
 * predicate. Unlike `isDocusignSignAction`, which answers what a *specific*
 * outcome resolved to, this covers the editor before any button is pressed: its
 * envelope action carries no outcome, so the toolbar is what says whether
 * signing is on offer. */
export function signsViaDocusign(action: Record<string, any>): boolean {
  if (action?.sign_method !== 'docusign') return false;
  const envelopeAction = action?.envelope_action;
  if (!envelopeAction || envelopeAction === 'sign') return true;
  const toolbar: string[] = action?.editor_toolbar_actions ?? [];
  return (
    envelopeAction === 'open_in_editor' &&
    (toolbar.includes('sign') || toolbar.includes('draft'))
  );
}

/**
 * The `trigger` a `document_review` logic rule receives once a review-screen
 * toolbar action has finalized and run its outcome. Built the same way for
 * the overlay viewer (button or `feathery.generateDocuments`) and for a
 * document-editor container, so rule code sees one shape.
 */
export function buildDocumentReviewTrigger({
  action,
  elementId,
  envelopes,
  envelopeAction,
  draft,
  result
}: {
  // The Generate Documents action config that opened the review.
  action: Record<string, any>;
  elementId: string;
  envelopes: { envelopeId: string }[];
  // The toolbar outcome finalize ran; a draft is reported as 'draft'.
  envelopeAction: string;
  draft: boolean;
  // The finalize response, when there was one.
  result?: Record<string, any> | null;
}): DocumentReviewTrigger {
  const trigger: DocumentReviewTrigger = {
    id: elementId,
    type: 'document_review',
    action: (draft ? 'draft' : envelopeAction) as DocumentReviewAction,
    envelopeIds: envelopes.map((envelope) => envelope.envelopeId),
    documentIds: action.documents ?? []
  };
  const files = result?.files;
  if (Array.isArray(files)) trigger.files = files;
  const docusignEnvelopeId = result?.docusign_envelope_id;
  if (docusignEnvelopeId) trigger.docusignEnvelopeId = docusignEnvelopeId;
  return trigger;
}
